import { Alert, Box, CircularProgress, Grid, Paper } from '@mui/material';
import type React from 'react';
import { useState } from 'react';
import { useAccount } from '../../contexts/AccountContext';
import { type BillingLedger, useBilling } from '../../contexts/BillingContext';
import { usePricing } from '../../contexts/PricingContext';
import { CreditsAndUsage } from './BillingManagement/CreditsAndUsage';
import { InvoiceDetailsDialog } from './BillingManagement/InvoiceDetailsDialog';
import { PastInvoices } from './BillingManagement/PastInvoices';
import { SeatLicensesAndBill } from './BillingManagement/SeatLicensesAndBill';
import {
  CostBreakdownByFunction,
  UsageByTeam,
  UsageByTender,
  UsageByUser,
} from './BillingManagement/UsageTables';
import { type Invoice, useBillingData } from './BillingManagement/useBillingData';
import ItemizedLedgerTable from './ItemizedLedgerTable';

interface BillingManagementPanelProps {
  overrideClientId?: string;
  overrideLedger?: BillingLedger | null;
  overrideSubscriptionStatus?: string;
  overrideSubscriptionEnd?:
    | string
    | Date
    | { toDate?: () => Date }
    | { seconds: number; nanoseconds: number }
    | null;
}

const BillingManagementPanel: React.FC<BillingManagementPanelProps> = ({
  overrideClientId,
  overrideLedger,
  overrideSubscriptionStatus,
  overrideSubscriptionEnd,
}) => {
  const { account: user } = useAccount();
  const { ledger, loading: billingLoading } = useBilling();
  const { pricingSettings, loading: pricingLoading, error: pricingError } = usePricing();

  const targetClientId = overrideClientId || user?.clientId;
  const targetLedger = overrideLedger !== undefined ? overrideLedger : ledger;
  const isBillingLoading = overrideLedger !== undefined ? false : billingLoading;
  const targetSubscriptionStatus =
    overrideSubscriptionStatus !== undefined
      ? overrideSubscriptionStatus
      : user?.subscription?.status;
  const targetSubscriptionEnd =
    overrideSubscriptionEnd !== undefined
      ? overrideSubscriptionEnd
      : user?.subscription?.current_period_end;

  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  const {
    activeUserCount,
    invoices,
    transactions,
    usersMap,
    teamsMap,
    loadingData,
    costsByFunction,
    costsByTeam,
    costsByUser,
    costsByTender,
  } = useBillingData(targetClientId, pricingSettings?.cost_per_credit_in_base || 0);

  const billingCurrency = 'AUD';

  const subscriptionEnd = targetSubscriptionEnd as { toDate?: () => Date } | string | undefined;
  const cycleEndDate = subscriptionEnd
    ? new Date(
        typeof (subscriptionEnd as { toDate?: () => Date }).toDate === 'function'
          ? (subscriptionEnd as { toDate: () => Date }).toDate()
          : (subscriptionEnd as string)
      )
    : null;

  const discountMultiplier = targetLedger?.discount
    ? Math.max(0, 1 - targetLedger.discount / 100)
    : 1;

  const totalSeatCost = pricingSettings
    ? (activeUserCount || 0) * pricingSettings.seat_license_cost_aud * discountMultiplier
    : 0;
  const totalUnbilledUsageCost = pricingSettings
    ? (targetLedger?.accumulated_usage || 0) * (pricingSettings.cost_per_credit_in_base || 0)
    : 0;
  const totalEstimatedBill = totalSeatCost + totalUnbilledUsageCost;

  if (isBillingLoading || pricingLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (pricingError || !pricingSettings) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {pricingError?.message ||
            'Pricing configuration is missing. Please contact support or configure it in the platform settings.'}
        </Alert>
      </Box>
    );
  }

  if (!targetLedger) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          No billing ledger found for this organization. A ledger will be created automatically upon
          your first billable action.
        </Alert>
      </Box>
    );
  }

  const isTrialing = targetSubscriptionStatus === 'trialing';

  return (
    <Box sx={{ p: 3 }}>
      {isTrialing && (
        <Alert severity="success" sx={{ mb: 3, fontSize: '1.05rem' }}>
          <strong>You are currently on a Free Trial.</strong> The usage and estimated costs shown
          below are for your information only. You will not be billed for any accumulated usage
          during your trial period.
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <CreditsAndUsage
            ledger={targetLedger}
            pricingSettings={pricingSettings}
            billingCurrency={billingCurrency}
            totalUnbilledUsageCost={totalUnbilledUsageCost}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <SeatLicensesAndBill
            ledger={targetLedger}
            pricingSettings={pricingSettings}
            cycleEndDate={cycleEndDate}
            activeUserCount={activeUserCount}
            totalSeatCost={totalSeatCost}
            totalUnbilledUsageCost={totalUnbilledUsageCost}
            totalEstimatedBill={totalEstimatedBill}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <PastInvoices
            invoices={invoices}
            loadingData={loadingData}
            onSelectInvoice={setSelectedInvoice}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <CostBreakdownByFunction loadingData={loadingData} costsByFunction={costsByFunction} />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <UsageByTender loadingData={loadingData} costsByTender={costsByTender} />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <UsageByTeam loadingData={loadingData} costsByTeam={costsByTeam} teamsMap={teamsMap} />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <UsageByUser loadingData={loadingData} costsByUser={costsByUser} usersMap={usersMap} />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 3 }}>
            <ItemizedLedgerTable
              clientId={targetClientId || ''}
              transactions={transactions}
              pricingSettings={pricingSettings}
            />
          </Paper>
        </Grid>
      </Grid>

      <InvoiceDetailsDialog
        invoice={selectedInvoice}
        onClose={() => setSelectedInvoice(null)}
        teamsMap={teamsMap}
        usersMap={usersMap}
      />
    </Box>
  );
};

export default BillingManagementPanel;
